import { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from "react-i18next";
import { WarningCircle } from '@phosphor-icons/react';

import { HSAContributionCardBoder, HSAContributionCardBackground, HSATitleHeader, HSAContributionLabel, HSACalculationSymbol, MaxContributionAmount, HSAContrinutionAmounts, DontWantContributeBanner, ContributionCardBanner, ContributionCardTitle, ContributionCardDescription, InfoBannerMaxContributionBanner } from "./HSAContributionStyled";
import {
  hsaContributionsIntial,
  updateContributeType,
  updateBalanceContributionAmount,
  updateScheduledContribution,
  updateResetAllEmployeeChanges
} from 'enrollment/services';
import { contributionDetails, renderTotalContributionEmployeePanel, regularEmployeeContributionAmount } from "../../utils/utils";
import { PsButtonTuner, PsNumericTuner, PsRadioTuner } from "stencilSetup";
import { I_DO_NOT_WANT_TO_CONTRIBUTE, CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD, CONTRIBUTION_FREQUENCY_TYPE_ANNUAL } from "../../utils";
import { convertCurrency } from "enrollment/utils";
import { LumpSumContribution } from "./LumpSumContribution";
import { EditContributionsDrawer } from "./EditContributionsDrawer";

let timeout:any = 0;

export const EmployeeContribution = (props: any) => {
    const dispatch = useDispatch();
    const { t } = useTranslation('pages.financial');
    const hsaContribution = useSelector(hsaContributionsIntial);
    const currentPlanYear = hsaContribution.current_plan_year;
    const remainingPaydays = hsaContribution?.employee_contribution_details?.remaining_paydays;
    const [validationMessages, setValidationMessages] = useState<any>({});
    const employeeContributionDetails = hsaContribution.employee_contribution_details;
    const contributionType = employeeContributionDetails?.subscriber_fsa_contribution_type;
    const [employeeContributionAmount, setEmployeeContributionAmount] = useState<any>('');
    const employeeMaxAmountContribution = employeeContributionDetails?.calculated_subscriber_max;
    const totalLumpSumContribution = regularEmployeeContributionAmount(hsaContribution, 'scheduled_subscriber_lump_sum_contribution');
    const minSubcriberAmount = hsaContribution?.employee_contribution_details?.calculated_subscriber_min;
    const submitContributionSchedule = hsaContribution?.disable_hsa_form;
    const disabled = `${submitContributionSchedule ? 'disabled' : ''}`;
    const contributionAdjustments = props.contributionAdjustments;
    const [contributionLimit, setContributionLimit] = useState(contributionDetails(contributionType, employeeMaxAmountContribution, remainingPaydays).maxContributionAmount);
    let exceededContributionLimit = Number(employeeContributionAmount) > Number(contributionLimit);
    const maximizeButtonDisabled = Number(employeeContributionAmount) >= Number(contributionLimit);
    const maxContributionWithoutLumpSum = contributionDetails(contributionType, Number(employeeMaxAmountContribution) - Number(totalLumpSumContribution), remainingPaydays).maxContributionAmount;

      useEffect(() => {
          const newLimit = Math.max(maxContributionWithoutLumpSum, Number(employeeContributionAmount));
          setContributionLimit(newLimit);
      }, [totalLumpSumContribution]);

      useMemo(() => {
        if(employeeContributionDetails.total_ee_contribution_amount){
          const defaultContributionAmount = contributionDetails(contributionType, employeeContributionDetails?.total_ee_contribution_amount, remainingPaydays).maxContributionAmount;
          if(defaultContributionAmount) {
            setEmployeeContributionAmount(defaultContributionAmount);
          };
        };
      }, [employeeContributionDetails.total_ee_contribution_amount]);

      useEffect(() => {
          const newLimit = maxContributionWithoutLumpSum;
          setContributionLimit(newLimit);
      }, [employeeContributionAmount]);

    useEffect(() => {
        if(hsaContribution.reset_all_employee_changes) {
            setEmployeeContributionAmount('');
            setValidationMessages({});
            setContributionLimit(contributionDetails(contributionType, employeeMaxAmountContribution, remainingPaydays).maxContributionAmount);
            dispatch(updateContributeType(''));
            dispatch(updateResetAllEmployeeChanges(false));
        }
    }, [hsaContribution]);

    const getBalanceContribution = (contributionType:string | undefined, maxContributionAmount:string, contributedAmount:number | string) => {
        const getSubscriberAnnualContributedAmount = (contributionDetails(contributionType, contributedAmount, remainingPaydays).employeeAnnualContributionAmount);
        const getBalanceContributionAmount = Number(maxContributionAmount) - getSubscriberAnnualContributedAmount;
        dispatch(updateBalanceContributionAmount(getBalanceContributionAmount));
        if (hsaContribution?.scheduled_contribution) {
            const getTotalSubscriberAmount = getSubscriberAnnualContributedAmount / remainingPaydays;
            const addingSubscriberAmount = hsaContribution.scheduled_contribution.map((item: any) => {
                return { ...item,
                  scheduled_subscriber_contribution: getTotalSubscriberAmount.toFixed(2),
                  scheduled_subscriber_lump_sum_contribution: contributionType === I_DO_NOT_WANT_TO_CONTRIBUTE ? '0' : item.scheduled_subscriber_lump_sum_contribution};
            });
            dispatch(updateScheduledContribution({ scheduled_contribution: addingSubscriberAmount, disable_hsa_form: false }));
        }
    };

    const handleChangeContribute = (event: any) => {
        const { value } = event.target;
        if (value) {
            let getMinmumContributionAmunt;
            const doNotContributeSelection = value === I_DO_NOT_WANT_TO_CONTRIBUTE;
            const employeeAmount = Number(employeeContributionDetails?.total_ee_contribution_amount) > 0 ? employeeContributionDetails?.total_ee_contribution_amount : minSubcriberAmount;
            if (!doNotContributeSelection && employeeAmount && Number(employeeAmount) > 0) {
                    getMinmumContributionAmunt = value === CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD ? (Number(employeeAmount) / remainingPaydays).toFixed(2) : Number(employeeAmount).toFixed(2);
            } else {
                getMinmumContributionAmunt = '';
            }
            event.stopPropagation();
            setEmployeeContributionAmount(getMinmumContributionAmunt);
            dispatch(updateContributeType(value));
            setContributionLimit(contributionDetails(value, employeeMaxAmountContribution, remainingPaydays).maxContributionAmount);
            getBalanceContribution(value, employeeMaxAmountContribution, getMinmumContributionAmunt ? getMinmumContributionAmunt : 0);
        }
    };

    const updateValidationMessages = (value: any) => {
        setValidationMessages({});
        const totalContribution = contributionDetails(contributionType, value, remainingPaydays).employeeAnnualContributionAmount + Number(totalLumpSumContribution);
        if(totalContribution > Number(employeeMaxAmountContribution)) {
            exceededContributionLimit = true;
        }
    };

    const addMaxContribution = () => {
        // Calculate the total lump sum contributions
        const totalLumpSumContribution = regularEmployeeContributionAmount(hsaContribution, 'scheduled_subscriber_lump_sum_contribution');
        // Calculate the maximum contribution amount without the lump sum contributions
        const maxContributionWithoutLumpSum = ((Number(employeeMaxAmountContribution) - Number(totalLumpSumContribution)).toFixed(2));
        const setMaxAmount = Number(contributionDetails(contributionType, maxContributionWithoutLumpSum, remainingPaydays)?.maxContributionAmount).toFixed(2);
        setEmployeeContributionAmount(setMaxAmount);
        getBalanceContribution(contributionType, employeeMaxAmountContribution, setMaxAmount);
        setValidationMessages({});
        setContributionLimit(Number(setMaxAmount));
    };

    const updateContributionAmount = () => {
        let minContributionAmount;
        if (contributionType === CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD) {
            minContributionAmount = Number(minSubcriberAmount) /  remainingPaydays;
        } else {
            minContributionAmount = Number(minSubcriberAmount);
        }
        if (minSubcriberAmount && (!employeeContributionAmount || Number(employeeContributionAmount) < minContributionAmount)) {
            setEmployeeContributionAmount(minContributionAmount.toFixed(2));
            getBalanceContribution(contributionType, employeeMaxAmountContribution, minContributionAmount);
        } else {
            const setContributionAmount = Number(employeeContributionAmount);
            setEmployeeContributionAmount(setContributionAmount.toFixed(2));
            getBalanceContribution(contributionType, employeeMaxAmountContribution, setContributionAmount);
        }
    };

    const handleContributionAmount = (e: { target: { value: any; }; }) => {
        const { value } = e.target;
        if (timeout) { clearTimeout(timeout); };
        timeout = setTimeout(() => {
            updateValidationMessages(value);
            setEmployeeContributionAmount(value);
            getBalanceContribution(contributionType, employeeMaxAmountContribution, Number(value));
        }, 500);
    };


    const getMaxEmployeeContribution = (employeeContributedAmount:number) => {
        if ((contributionType === CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD) && Number(employeeMaxAmountContribution) < Number(employeeContributionAmount) * remainingPaydays) {
            return "$_";
        } else if ((contributionType === CONTRIBUTION_FREQUENCY_TYPE_ANNUAL) &&  Number(employeeMaxAmountContribution)  < Number(employeeContributionAmount)) {
           return "$_";
        } else {
            return convertCurrency(String(employeeContributedAmount));
        }
    };

    const getContributionAmountBalanceMesssage = () => {
        if (!submitContributionSchedule && employeeContributionAmount) {
            let calculatedContributionAmount;
            if (contributionType === CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD) {
                calculatedContributionAmount = employeeContributionAmount * remainingPaydays;
            } else {
                calculatedContributionAmount = employeeContributionAmount;
            }
            const lumpSumSchduledContributions = hsaContribution.scheduled_contribution;
            let lumpSumAmountOfSum = 0;
            for (let i = 0; i < lumpSumSchduledContributions.length; i++) {
              if (lumpSumSchduledContributions[i].scheduled_subscriber_lump_sum_contribution) {
                lumpSumAmountOfSum = lumpSumAmountOfSum + Number(lumpSumSchduledContributions[i].scheduled_subscriber_lump_sum_contribution);
              }
            };
            const checkBalanceContributionAmount = Number(employeeMaxAmountContribution) - (Number(calculatedContributionAmount) + lumpSumAmountOfSum);
            const balanceContributionDescription = checkBalanceContributionAmount <= 0 ? t('employeeContribution.maximizedContributionAmountDescription') : t('employeeContribution.balanceContributionAmountDescription', { employeeBalanceContributionAmount: convertCurrency(String(checkBalanceContributionAmount)) });
            return (
                <div className={isScreenSmall ? "my-4": ""}>
                    <InfoBannerMaxContributionBanner>
                        <div data-testid={checkBalanceContributionAmount <= 0 ? 'contributionAmountMaximizedBanner' : 'contributionAmountBalanceBanner' } className="d-flex justify-content-between align-items-center">
                            <div className="mr-2"> <WarningCircle size={24} /></div>
                            <div>
                                <div className="semiBold pb-1">{t(checkBalanceContributionAmount <= 0 ? 'employeeContribution.maximizedContributionAmountTitle' : 'employeeContribution.balanceContributionAmountTitle')}</div>
                                <div className="title-small">{balanceContributionDescription}</div>
                            </div>
                        </div>
                    </InfoBannerMaxContributionBanner>
                </div>);
        }
    };
   const isScreenSmall = window.innerWidth < 922;
    
    const maximizeContributionBtn = () => {
      return (
        <div className={isScreenSmall ? "pt-4" : "py-3_5"}>
          <PsButtonTuner
            className={isScreenSmall ? 'mt-4 ml-2 maximize-btn' : ''}
            buttonId="max-my-contribution-button"
            data-testid="max-my-contribution-button"
            level="secondary"
            label={isScreenSmall ? 'Maximize' : 'Maximize My Contribution'}
            onClick={addMaxContribution}
            disabled={maximizeButtonDisabled}
          />
        </div>
      );
    };

    const remainingPayPeriod = () => {
  return (
    <>
      <div className={isScreenSmall ? 'd-none' : 'pt-3_5'}>
        <HSACalculationSymbol>
          {
            contributionDetails(contributionType, 0, 0)
              ?.contributionCalculationType
          }
        </HSACalculationSymbol>
      </div>
      <div className={isScreenSmall ? 'd-flex contributionSection' : ''}>
        <HSAContributionLabel>
          <div>{t('employeeContribution.remainingPayPeriodText')}</div>
        </HSAContributionLabel>
        <div className={isScreenSmall ? 'contributionSection px-1' : 'd-none'}>
          <HSACalculationSymbol>:</HSACalculationSymbol>
        </div>
        <HSAContrinutionAmounts>
          <div className={isScreenSmall ? 'pl-2 hsaContributionAmt' : 'py-2_5'}>
            {remainingPaydays}
          </div>
        </HSAContrinutionAmounts>
      </div>
      <div className={isScreenSmall ? 'd-none' : 'pt-3_5'}>
        <HSACalculationSymbol>=</HSACalculationSymbol>
      </div>
      <div className={isScreenSmall ? 'd-flex pb-3 border-bottom' : ''} >
        <HSAContributionLabel>
          <div>
            {t(
              contributionDetails(contributionType, 0, 0)
                ?.perPayPeriodAmountLabel
            )}
          </div>
        </HSAContributionLabel>
        <div className={isScreenSmall ? 'contributionSection px-1' : 'd-none'} >
          <HSACalculationSymbol>:</HSACalculationSymbol>
        </div>
        <HSAContrinutionAmounts>
          <div className={isScreenSmall ? 'pl-2 hsaContributionAmt' : 'py-2_5'}>
            {employeeContributionAmount
              ? getMaxEmployeeContribution(
                contributionDetails(
                  contributionType,
                  employeeContributionAmount,
                  remainingPaydays
                ).employeePPPContributionAmount
              )
              : '$_'}
          </div>
        </HSAContrinutionAmounts>
      </div>
    </>
  );
};    
  return (
        <div className="px-3_5 px-md-5 mb-4" data-testid="employee-contribution-card">
            <HSAContributionCardBoder>
                <HSAContributionCardBackground>
                    <div className="text-center py-3_5">
                        <div className={isScreenSmall ? "title-medium font-weight-bold": "title-large"}>
                          {renderTotalContributionEmployeePanel(hsaContribution, employeeMaxAmountContribution)}
                        </div>
                        <div className={isScreenSmall ? "title-small  pt-2 pb-1 semiBold" : "title-medium pt-2 pb-1 semiBold"}>{t('employeeContribution.subTitle', {currentPlanYear})} </div>
                        <div className="title-small pb-2">{t('employeeContribution.description')}</div>
                    </div>
                    {(contributionAdjustments.employeeAdjusted && !contributionAdjustments.employeeisZero) &&
                        <ContributionCardBanner data-testid="employee-contribution-adjusted">
                            <ContributionCardTitle>{t('contributionAdjustments.title')}</ContributionCardTitle>
                            <ContributionCardDescription>{t('contributionAdjustments.employeeAdjustedDescription')}</ContributionCardDescription>
                        </ContributionCardBanner>
                    }
                </HSAContributionCardBackground>
                <div className="pt-4 pb-2 px-3">
                    <HSATitleHeader>
                        <div className={`semiBold ${disabled}`}>{t('employeeContribution.contributionOption')}</div>
                    </HSATitleHeader>
                    <div>
                        <PsRadioTuner
                            inputId={CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD}
                            name="contribution_type"
                            label={t('employeeContribution.perPayPeriodLabel')}
                            checked={contributionType === CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD}
                            value={CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD}
                            data-testid={CONTRIBUTION_FREQUENCY_TYPE_PER_PAY_PERIOD}
                            onClick={handleChangeContribute}
                            className={disabled}
                        />
                        <PsRadioTuner
                            inputId={CONTRIBUTION_FREQUENCY_TYPE_ANNUAL}
                            name="contribution_type"
                            label={t('employeeContribution.annualLabel')}
                            checked={contributionType === CONTRIBUTION_FREQUENCY_TYPE_ANNUAL}
                            value={CONTRIBUTION_FREQUENCY_TYPE_ANNUAL}
                            data-testid={CONTRIBUTION_FREQUENCY_TYPE_ANNUAL}
                            onClick={handleChangeContribute}
                            className={disabled}
                        />
                        <PsRadioTuner
                            inputId={I_DO_NOT_WANT_TO_CONTRIBUTE}
                            name="contribution_type"
                            label={isScreenSmall  ? t('employeeContribution.dontContributeLabel') : t('employeeContribution.iDoNotWantContributeLabel')}
                            checked={contributionType === I_DO_NOT_WANT_TO_CONTRIBUTE}
                            value={I_DO_NOT_WANT_TO_CONTRIBUTE}
                            data-testid={I_DO_NOT_WANT_TO_CONTRIBUTE}
                            onClick={handleChangeContribute}
                            className={disabled}
                        />
                    </div>

                    <div className={`${submitContributionSchedule ? '' : `${isScreenSmall ? 'mt-3' : 'border-top mt-3'}`}`}>
                        {contributionType && contributionType !== I_DO_NOT_WANT_TO_CONTRIBUTE && !submitContributionSchedule &&
                        <>
                            <div className={isScreenSmall ? "mb-3" : "d-flex justify-content-between align-items-center mt-3" }>
                                <div className={isScreenSmall ? "d-flex align-items-center justify-content-start" :" "}>         
                                    <div>
                                        <PsNumericTuner
                                            data-testid="max-contribution-amount-input"
                                            inputId="max-contribution-amount-input"
                                            className={isScreenSmall ?"perPayPeriodContribution":""}
                                            name="choose-pcp-location-input"
                                            label={t(contributionDetails(contributionType, 0, 0)?.contributionLabels)}
                                            value={employeeContributionAmount}
                                            onInput={(e) => handleContributionAmount(e as any)}
                                            onBlur={updateContributionAmount}
                                            acceptDecimal={true}
                                            icon-left
                                            icon="currency-dollar"
                                            min={JSON.stringify(Number(hsaContribution?.employee_contribution_details?.calculated_subscriber_min))}
                                            isInvalid={employeeContributionAmount !== "" && exceededContributionLimit}
                                            helpText={exceededContributionLimit ? "" : convertCurrency(JSON.stringify(contributionLimit)) + t('employeeContribution.maxContributionText')}
                                        />
                                        <MaxContributionAmount>
                                            {exceededContributionLimit &&
                                                <div className="pt-1 invalid-feedback errorMessageMaxContribution">
                                                    {t('employeeContribution.exceedEmployeeMaximum')}
                                                </div>}
                                            <input type="hidden" value={convertCurrency(JSON.stringify(Number(contributionDetails(contributionType, employeeMaxAmountContribution, remainingPaydays)?.maxContributionAmount))) + t('employeeContribution.maxContributionText')} data-testid="max-contribution-amount-value"/>
                                        </MaxContributionAmount>
                                    </div>
                                    {isScreenSmall && maximizeContributionBtn()}
                                </div>
                                {!isScreenSmall && remainingPayPeriod()}
                            </div>
                            {isScreenSmall && remainingPayPeriod()}
                            {!isScreenSmall && maximizeContributionBtn()}
                        </>}
                        {getContributionAmountBalanceMesssage()}
                        {(!contributionType || (contributionType && contributionType !== I_DO_NOT_WANT_TO_CONTRIBUTE)) && <LumpSumContribution validationMessages={validationMessages} setValidationMessages={setValidationMessages} onResetChanges={props?.onResetChanges}/>}
                        </div>
                    {contributionType && contributionType !== I_DO_NOT_WANT_TO_CONTRIBUTE && <EditContributionsDrawer />}
                </div>
                {contributionType && contributionType === I_DO_NOT_WANT_TO_CONTRIBUTE && !submitContributionSchedule &&<>
                    <DontWantContributeBanner>
                        <div className="px-4 py-3 mt-3" data-testid="do-not-contribute-warning-text">
                            {t('employeeContribution.doNotWantContributeDescription')}
                        </div>
                    </DontWantContributeBanner>
                </>}
            </HSAContributionCardBoder>
        </div>
    );
};
